import { HtsReadinessPillarKey } from "../constants";
import { HtsReadinessPillarConfig } from "../schemas";

/**
 * Integrity's dimension keys. Named rather than inlined because
 * htsReadinessScoring.ts addresses these three individually — two are scored
 * deterministically in code and the third is the only one handed to the
 * model, and the mandatory-review gate reads two of them back by key. A bare
 * literal there would drift silently against this config (the score would
 * land under a stale key and fall back to 0 / "no data").
 */
export const HtsIntegrityDimensionKey = {
  ConductHistory: "conductHistory",
  HonestyCompleteness: "honestyCompleteness",
  InsightAccountability: "insightAccountability",
} as const;
export type HtsIntegrityDimensionKey =
  (typeof HtsIntegrityDimensionKey)[keyof typeof HtsIntegrityDimensionKey];

/**
 * The one pillar not driven by a dedicated READINESS topic — its data source
 * is Section B's existing suitability classification/flag/consistency-flag/
 * perfect-person-probe output (EVT-78). Present whenever the submission has
 * at least one SUITABILITY-kind answered question (see
 * htsReadinessScoring.ts's presence check), not via readinessPillars mapping.
 *
 * `conductHistory` and `honestyCompleteness` are computed deterministically in
 * code from that existing data (never trusted from a model) — only
 * `insightAccountability` is genuinely qualitative and passed to the
 * htsReadinessScorer agent. Kept here anyway (dimensions array below,
 * including the two deterministic ones) so the anchor text is documented in
 * one place and the UI can render all three consistently.
 */
export const integrity: HtsReadinessPillarConfig = {
  key: HtsReadinessPillarKey.INTEGRITY,
  label: "Integrity",
  definition:
    "Personal life choices over time — lesser criminal actions, illegal substances use, online conduct, financial dishonesty — and, critically, how honestly and completely the candidate discloses them. Legal cannabis and alcohol use, financial hardship, and police contact without a finding are not scored as conduct. Substance dependence is a protected ground and is neither asked nor scored. In this rubric the disclosure itself is scored: two candidates with identical histories can score very differently based on candour, which is precisely what agencies screen for.",
  dataSourceNote:
    "Derived — from Section B as a whole (crime and convictions, online conduct, police contact, drugs and alcohol, finances, driving convictions), plus the perfect-person probe and the consistency-check engine. Never asked as a self-rating.",
  dimensions: [
    {
      // Descriptive only — computeConductHistory scores this deterministically
      // and never reads these anchors. Editing them doesn't change scoring;
      // the actual policy lives in each suitability topic's
      // followUpTriggers/crossCuttingNotes (personalConductHistory.ts, etc).
      key: HtsIntegrityDimensionKey.ConductHistory,
      label: "Conduct History",
      weight: 0.4,
      anchors: {
        0: "Conviction, or admitted conduct in HTS high-priority territory.",
        25: "Admitted lesser criminal actions that are recent (within the last year) or part of a pattern, or a charge that hasn't yet been resolved by the courts.",
        50: "A pattern of lesser actions, or dated items between one and five years ago with no clear pattern.",
        75: "A single isolated lesser action more than five years ago, with no indication of a pattern.",
        100: "No conduct concerns across all of Section B, confirmed by a consistency check with no open flag.",
      },
    },
    {
      key: HtsIntegrityDimensionKey.HonestyCompleteness,
      label: "Honesty & Completeness of Disclosure",
      weight: 0.35,
      anchors: {
        0: "A material inconsistency or omission caught by the consistency check.",
        25: "Vague or evasive across probes; the three-probe cap was reached without a classifiable account.",
        50: "Complete, but only after probing pulled it out piece by piece.",
        75: "Full and forthcoming with minimal probing.",
        100: "Spontaneous, complete, specific disclosure throughout — including the perfect-person probe. A candidate who reports nothing at the probe is not scored lower for that; the probe scores tone and consistency, not content.",
      },
    },
    {
      key: HtsIntegrityDimensionKey.InsightAccountability,
      label: "Insight & Accountability",
      weight: 0.25,
      anchors: {
        0: "Denies any personal part where the facts in Section B show one, or minimizes disclosed conduct.",
        25: "Acknowledges facts but accepts little responsibility.",
        50: "Accepts responsibility with limited reflection on cause or change.",
        75: "Owns the conduct and articulates what changed, with some supporting evidence.",
        100: "Full ownership, specific lessons, and demonstrated changed behaviour over time.",
      },
    },
  ],
  topIndicators: [
    "A complete disclosure the first time asked",
    "A specific account of what changed",
    "The perfect-person probe answered with an actual small human story",
  ],
  scoringNotes: [
    "Conduct History cannot score 100 on a blank Section B until the perfect-person probe has run.",
    "An open consistency-check flag caps Honesty & Completeness at 25 until a human resolves it.",
    "Global mandatory review trigger: Conduct History ≤25 or Honesty & Completeness ≤25 withholds the readiness summary pending evaluator review.",
    "A pending charge is a suitability-classifier review trigger (see criminalChargesConvictions.ts), not a determination of guilt — it lands Conduct History at 25 via the standard flagged-disclosure path, which withholds the whole profile for evaluator review like any other flagged item, rather than scoring it as if the outcome were already known.",
    "Not applicable where Conduct History scores 100 — whether automatically or by a reviewer's override. In that case Insight & Accountability's weight drops to 0% (still scored and shown, for coaching, but not counted) and the remaining two dimensions are re-weighted proportionally (Conduct History 53%, Honesty & Completeness 47%).",
    "The stream-conditional undetected-offence answer feeds Conduct History as a flag and score input; like every other answer, its free-text content is stored encrypted and is evaluator-facing only, never surfaced to the candidate.",
  ],
};
